import {
  AlertCircle,
  ArrowRight,
  Building2,
  CreditCard,
  Key,
  LayoutDashboard,
  Receipt,
} from "lucide-react";
import { useState } from "react";
import sullyWordmark from "@/assets/sully-wordmark.png";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/store/auth";

const SELF_SERVE_SIGNUP_STEPS = [
  {
    icon: Building2,
    title: "Create your organization",
    description: null,
  },
  {
    icon: Key,
    title: "Set up API keys and start building",
    description: null,
  },
] as const;

const LEGACY_BILLING_STEPS = [
  {
    icon: Receipt,
    title: "Review pricing",
    description: "Usage-based rates, straight from our live rate card.",
  },
  {
    icon: CreditCard,
    title: "Add a credit card",
    description: "A credit card must be saved to activate your account.",
  },
  {
    icon: LayoutDashboard,
    title: "Start building",
    description: "Head straight into the dashboard to create API keys.",
  },
] as const;

const EXISTING_ORGANIZATION_STEPS = [
  {
    icon: Building2,
    title: "Join your organization",
    description: "Use the workspace your organization invited you to.",
  },
  {
    icon: LayoutDashboard,
    title: "Start building",
    description: "Head straight into the dashboard to access your team.",
  },
] as const;

/**
 * First NUX step: explains either immediate self-serve access or the preserved
 * billing contract for an existing pending organization.
 *
 * @param requiresBillingSetup Whether the selected legacy organization must
 * complete pricing and card setup before activation.
 * @param hasOrganization Whether the authenticated user already belongs to an
 * organization, including through a WorkOS invitation.
 * @param requiresTermsAcceptance Whether this newly provisioned user must consent.
 * @param isSubmitting Whether the consent request is in flight.
 * @param errorMessage Consent persistence error shown without advancing.
 * @param onContinue Invoked when the user activates Get Started; the parent owns
 * the resulting step transition.
 * @returns The welcome content and progression action.
 */
export const WelcomeStep = ({
  requiresBillingSetup,
  hasOrganization,
  requiresTermsAcceptance,
  isSubmitting,
  errorMessage,
  onContinue,
}: {
  requiresBillingSetup: boolean;
  hasOrganization: boolean;
  requiresTermsAcceptance: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  onContinue: () => void;
}) => {
  const signOut = useAuthStore((s) => s.signOut);
  const [hasCheckedConsent, setHasCheckedConsent] = useState(false);
  const signupSteps = hasOrganization
    ? EXISTING_ORGANIZATION_STEPS
    : requiresBillingSetup
      ? LEGACY_BILLING_STEPS
      : SELF_SERVE_SIGNUP_STEPS;

  return (
    <>
      <div className="w-full space-y-8">
        <div className="flex flex-col items-start gap-8 text-left">
          <span className="onboarding-wordmark">
            <img src={sullyWordmark} alt="Sully" />
          </span>
          <h2 className="text-[32px] font-medium leading-[1.2] tracking-tight">
            Build using the Sully API platform.
          </h2>
        </div>

        <ul className="space-y-6">
          {signupSteps.map(({ icon: Icon, title, description }) => (
            <li
              key={title}
              className={`flex space-x-4 ${description ? "items-start" : "items-center"}`}
            >
              <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border">
                <Icon className="size-5 text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-medium leading-6">{title}</h3>
                {description && (
                  <p className="text-sm text-muted-foreground">{description}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {requiresTermsAcceptance && (
        <label className="flex items-start gap-2.5 text-[13px] leading-6 text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            aria-label="I accept the Sully.ai Terms of Use, BAA, and Privacy Policy."
            checked={hasCheckedConsent}
            onChange={(event) => setHasCheckedConsent(event.target.checked)}
            disabled={isSubmitting}
            className="mt-0.5 size-4 rounded border-input accent-primary"
          />
          <span>
            I accept the Sully.ai{" "}
            <a
              href="https://app.sully.ai/copilot/sullyai-tos.pdf"
              target="_blank"
              rel="noreferrer"
              className="text-primary underline underline-offset-2"
            >
              Terms of Use
            </a>
            ,{" "}
            <a
              href="/baa.pdf"
              target="_blank"
              rel="noreferrer"
              className="text-primary underline underline-offset-2"
            >
              BAA
            </a>
            , and{" "}
            <a
              href="https://www.sully.ai/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="text-primary underline underline-offset-2"
            >
              Privacy Policy
            </a>
            .
          </span>
        </label>
      )}

      {errorMessage && (
        <p
          className="flex items-center gap-2 text-sm text-destructive"
          role="alert"
        >
          <AlertCircle className="size-4 shrink-0" />
          {errorMessage}
        </p>
      )}

      <div className="flex flex-col items-center gap-3">
        <Button
          onClick={onContinue}
          className="group h-11 w-full px-8 text-sm font-medium"
          size="lg"
          disabled={
            (requiresTermsAcceptance && !hasCheckedConsent) || isSubmitting
          }
        >
          {isSubmitting ? "Saving..." : "Get Started"}
          <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Button>

        <button
          type="button"
          onClick={() => signOut({ redirectUrl: window.location.origin })}
          disabled={isSubmitting}
          className="text-xs text-muted-foreground transition-colors hover:text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        >
          Logout
        </button>
      </div>

      {!hasOrganization && (
        <p className="border-t pt-6 text-[13px] leading-6 text-muted-foreground">
          Joining an existing team? Use the invite link from your organization
          admin instead of creating a new organization.
        </p>
      )}
    </>
  );
};
